import { UnrecoverableError } from './UnrecoverableError'

import {
  APP_DEPENDENCY_ERRORS,
  AppDependencyErrors,
} from '@/constants/errors/code'
import { ErrorPrefix } from '@/constants/errors/prefix'
import type { KeyofUnion } from '@/types/type-handlers'
import { getStatusFromApiError } from '@/utils/get-status-from-api-error'

/**
 * This class represents unrecoverable errors related to app/page dependencies.
 *
 * It provides specific details to build an app/page dependency error
 * code to the {@link UnrecoverableError} class.
 *
 * @example
 * ```ts
 * throw new AppDependencyUnrecoverableError('PAAS_ORDER_FAILED', originalError)
 * // This will create an error with a code like 'AP_500_00001_HA'
 * // The fictional example code breakdown is as follows:
 * // - 'AP' is the prefix for app dependency errors.
 * // - '500' is the HTTP status code extracted from the original error (if available).
 * // - '00001' is the specific error code for 'PAAS_ORDER_FAILED'.
 * // - 'HA' is the suffix for the current page (here, 'Hp All-In').
 * ```
 *
 * @see ERRORS.md and ERROR_CODES.md for more details.
 */
export class AppDependencyUnrecoverableError extends UnrecoverableError {
  readonly prefix = ErrorPrefix.APP

  /**
   * @param errorCodeType The type of error code to be used
   *   from {@link APP_DEPENDENCY_ERRORS} — All keys of the union of all possible error codes.
   * @param errorPayload optional original error payload. It auto extracts the status code if it's an HTTP error.
   */
  constructor(
    readonly errorCodeType?: KeyofUnion<
      AppDependencyErrors[keyof AppDependencyErrors]
    >,
    errorPayload?: unknown,
  ) {
    super(errorPayload)
  }

  /**
   * Returns the code part of the error code based on the dependency parameters.
   *
   * The code is obtained by getting the current page suffix using {@link getPageSuffix},
   * and using the `errorCodeType` to map the code value in {@link APP_DEPENDENCY_ERRORS}
   */
  protected getCode(): string | undefined {
    if (!this.errorCodeType) return

    const suffix = this.getPageSuffix()
    const codes = APP_DEPENDENCY_ERRORS[suffix as keyof AppDependencyErrors]

    if (codes && this.errorCodeType in codes)
      return codes[this.errorCodeType as keyof typeof codes]
  }

  protected getStatusCode() {
    return getStatusFromApiError(this.errorPayload)
  }
}
